import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { RotateCcw, Route, BookOpen, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, isNetworkError } from "@/lib/api";
import { loadContent, saveQuiz, getQuiz, gradeLocally, markTopicDoneLocally } from "@/lib/offlineContent";
import { enqueue } from "@/lib/offlineSync";
import { useAuth } from "@/context/AuthContext";
import { GeneratingState } from "@/components/GeneratingState";
import { QuizRunner } from "@/components/QuizRunner";
import { ScoreRing } from "@/components/ScoreRing";
import { ResultReview } from "@/components/ResultReview";
import { Celebration } from "@/components/Celebration";

export default function Assessment() {
  const { lang, level, topic } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [celebrate, setCelebrate] = useState(false);

  const load = useCallback(async () => {
    setError("");
    setQuiz(null);
    setResult(null);
    try {
      setQuiz(await loadContent({ lang, level, topic, endpoint: "/assessments/generate", get: getQuiz, save: saveQuiz }));
    } catch (e) {
      setError(e.response ? errMsg(e) : e.message);
    }
  }, [lang, level, topic]);

  useEffect(() => { load(); }, [load]);

  const submit = async (answers) => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/assessments/submit", { quiz_id: quiz.quiz_id, answers, offline: !!quiz.fromCache });
      setResult(data);
      setUser(data.user);
      if (data.passed) setCelebrate(true);
    } catch (e) {
      if (!isNetworkError(e) || !quiz.answer_key) return toast.error(errMsg(e));
      const local = gradeLocally(quiz, answers);
      const isFinal = topic === "final";
      const xp = local.passed ? (isFinal ? 150 : 50) : 5;
      await enqueue("assessment_submit", { quiz_id: quiz.quiz_id, answers, offline: true });
      if (local.passed) await markTopicDoneLocally(lang, level, topic);
      setUser((u) => ({ ...u, xp: u.xp + xp }));
      setResult({ ...local, pass_mark: 70, xp_earned: xp, kind: isFinal ? "final" : "topic", next: null, offline: true });
      if (local.passed) setCelebrate(true);
    } finally {
      setSubmitting(false);
    }
  };

  const goNext = () => {
    const n = result?.next;
    if (n && n.topic_id !== "final") navigate(`/lesson/${lang}/${n.level}/${n.topic_id}`);
    else navigate(`/learn/${lang}`);
  };

  if (!quiz) return <GeneratingState title="Preparing your assessment" error={error} onRetry={load} />;
  if (!result)
    return (
      <div className="px-4 py-10">
        <p className="text-center caption mb-6">{quiz.topic.title} · Assessment</p>
        <QuizRunner questions={quiz.questions} lang={lang} onSubmit={submit} submitting={submitting} onExit={() => navigate(`/learn/${lang}`)} />
      </div>
    );

  const isFinal = result.kind === "final";
  return (
    <div className="max-w-2xl mx-auto px-4 py-10 animate-fade-up" data-testid="assessment-result">
      <div className="flex flex-col items-center text-center">
        <ScoreRing score={result.score} passed={result.passed} />
        <h1 data-testid="assessment-result-title" className="font-display text-3xl font-extrabold text-slate-900 mt-6">
          {result.passed ? (isFinal ? "Level passed!" : "Topic passed!") : "So close, keep going"}
        </h1>
        <p className="text-slate-600 mt-2">
          {result.passed ? `You earned ${result.xp_earned} XP.` : `You need ${result.pass_mark}% to pass. Review the lesson and try again, there's no rush.`}
        </p>
        {result.offline && <p data-testid="assessment-offline-note" className="mt-2 text-sm font-semibold text-slate-800 bg-slate-100 rounded-full px-4 py-1.5">Scored on this device. It will sync when you're back online.</p>}
        <div className="flex flex-wrap gap-2 justify-center mt-6">
          {result.passed ? (
            <button data-testid="result-next-button" onClick={goNext} className="btn-primary">Next step <ArrowRight size={18} /></button>
          ) : (
            <>
              <button data-testid="result-retry-button" onClick={load} className="btn-primary"><RotateCcw size={18} /> Retry</button>
              {topic !== "final" && <button data-testid="result-review-lesson-button" onClick={() => navigate(`/lesson/${lang}/${level}/${topic}`)} className="btn-ghost"><BookOpen size={18} /> Review lesson</button>}
            </>
          )}
          <button data-testid="result-map-button" onClick={() => navigate(`/learn/${lang}`)} className="btn-ghost"><Route size={18} /> Path</button>
        </div>
      </div>
      <h2 className="font-display text-xl font-bold text-slate-900 mt-12 mb-4">Review your answers</h2>
      <ResultReview results={result.results} />
      <Celebration
        open={celebrate}
        onClose={() => setCelebrate(false)}
        title={isFinal ? `${level[0].toUpperCase() + level.slice(1)} level complete!` : "Topic unlocked!"}
        subtitle={isFinal ? (result.unlocked_level ? `You've unlocked ${result.unlocked_level}. ¡Fantástico!` : "You've mastered every level. Incredible!") : `You scored ${result.score}%. The next topic is now open.`}
        xp={result.xp_earned}
      >
        <button data-testid="celebration-next-button" onClick={goNext} className="btn-primary">Keep going <ArrowRight size={18} /></button>
        <button data-testid="celebration-close-button" onClick={() => setCelebrate(false)} className="btn-ghost">See my answers</button>
      </Celebration>
    </div>
  );
}
